import { useState, useCallback, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';

interface CaseReportPropertyListProps {
  items: any[];
  isReadOnly?: boolean;
  ownerOptions?: any[];
  reportedAsOptions?: any[];
  onChange?: (items: any[]) => void;
  onAdd?: () => void;
  onEdit?: (item: any) => void;
}

export function CaseReportPropertyList({
  items,
  isReadOnly = false,
  ownerOptions = [],
  reportedAsOptions = [],
  onChange,
  onAdd,
  onEdit,
}: CaseReportPropertyListProps) {
  const { theme } = useTheme();

  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; item: any }>({
    open: false,
    item: null,
  });

  const hasItems = useMemo(() => {
    return items && items.length > 0;
  }, [items]);

  const addProperty = useCallback(() => {
    onAdd?.();
  }, [onAdd]);

  const editProperty = useCallback((item: any) => {
    onEdit?.(item);
  }, [onEdit]);

  const confirmDelete = useCallback((item: any) => {
    setDeleteConfirm({ open: true, item });
  }, []);

  const doDelete = useCallback(() => {
    const item = deleteConfirm.item;
    if (item) {
      onChange?.(items.filter((i: any) => i.tempid !== item.tempid));
    }
    setDeleteConfirm({ open: false, item: null });
  }, [deleteConfirm.item, items, onChange]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirm({ open: false, item: null });
  }, []);

  const formatValue = useCallback((value: any) => {
    if (value == null || value.value == null) return '\u2014';
    const dollars = value.value / 100;
    return '$' + dollars.toFixed(2);
  }, []);

  const getOwnerName = useCallback((item: any) => {
    if (item.npsdPersonOwner) {
      const owner = ownerOptions.find((o: any) => o.id === item.npsdPersonOwner + '_P' || o.id === item.npsdPersonOwner);
      if (owner) return owner.name || owner.text;
    }
    if (item.npsdBusinessOwner) {
      const owner = ownerOptions.find((o: any) => o.id === item.npsdBusinessOwner + '_B' || o.id === item.npsdBusinessOwner);
      if (owner) return owner.name || owner.text;
    }
    if (item.npsdClientOwner) {
      const owner = ownerOptions.find((o: any) => o.id === item.npsdClientOwner + '_C' || o.id === item.npsdClientOwner);
      if (owner) return owner.name || owner.text;
    }
    return '\u2014';
  }, [ownerOptions]);

  const getReportedAsName = useCallback((item: any) => {
    if (!item.reportedAs) return '';
    const reportedAs = reportedAsOptions.find((o: any) =>
      o.code === item.reportedAs || o.code === parseInt(item.reportedAs)
    );
    return reportedAs ? reportedAs.value : '';
  }, [reportedAsOptions]);

  const getReportedAsClass = useCallback((item: any) => {
    if (!item.reportedAs) return 'status-unknown';
    const code = parseInt(item.reportedAs);
    if (code === 1) return 'status-lost';
    if (code === 2) return 'status-found';
    if (code === 3) return 'status-stolen';
    if (code === 4) return 'status-damaged';
    if (code === 5) return 'status-recovered';
    return 'status-other';
  }, []);

  return (
    <div className={'case-report-property-list' + (theme ? ' ' + theme : '')}>
      <div className="header">
        <h1>Property</h1>
        {!isReadOnly && (
          <button
            onClick={addProperty}
            className="btn-add"
            type="button"
          >
            <i className="fas fa-plus"></i> PROPERTY
          </button>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="table-container">
        {!hasItems ? (
          <div className="empty-state">
            <p>No property items added</p>
          </div>
        ) : (
          <div className="property-items">
            {items.map((item) => (
              <div key={item.tempid} className="property-item">
                {/* Primary Row */}
                <div className="property-primary">
                  <div className="primary-status">
                    <span className={'status-badge ' + getReportedAsClass(item)}>
                      {getReportedAsName(item) || 'Unknown'}
                    </span>
                  </div>
                  <div className="primary-item">
                    <div className="item-name">{item.item || 'Unknown Item'}</div>
                  </div>
                  <div className="primary-owner">{getOwnerName(item)}</div>
                  <div className="primary-value">{formatValue(item.estimatedValue)}</div>
                  {!isReadOnly && (
                    <div className="primary-actions">
                      <button className="btn-edit" onClick={() => editProperty(item)}>Edit</button>
                      <button className="btn-delete" onClick={() => confirmDelete(item)}>Delete</button>
                    </div>
                  )}
                </div>
                {/* Detail Row - All Fields */}
                <div className="property-details">
                  <div className="detail-grid">
                    <div className="detail-field">
                      <span className="detail-label">Brand</span>
                      <span className="detail-value">{item.brand || '\u2014'}</span>
                    </div>
                    <div className="detail-field">
                      <span className="detail-label">Model</span>
                      <span className="detail-value">{item.model || '\u2014'}</span>
                    </div>
                    <div className="detail-field">
                      <span className="detail-label">Year</span>
                      <span className="detail-value">{item.year || '\u2014'}</span>
                    </div>
                    <div className="detail-field">
                      <span className="detail-label">Colors</span>
                      <span className="detail-value">{item.colors || '\u2014'}</span>
                    </div>
                    <div className="detail-field">
                      <span className="detail-label">Serial #</span>
                      <span className="detail-value">{item.serialNum || '\u2014'}</span>
                    </div>
                    <div className="detail-field">
                      <span className="detail-label">Location</span>
                      <span className="detail-value">{item.location || '\u2014'}</span>
                    </div>
                    <div className="detail-field">
                      <span className="detail-label">Storage</span>
                      <span className="detail-value">{item.storageLoc || '\u2014'}</span>
                    </div>
                    <div className="detail-field">
                      <span className="detail-label">Damages</span>
                      <span className="detail-value">{item.damages || '\u2014'}</span>
                    </div>
                    <div className="detail-field detail-field-full">
                      <span className="detail-label">Notes</span>
                      <span className="detail-value multiline-text">{item.officerNotes || '\u2014'}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="mobile-cards">
        {!hasItems ? (
          <div className="empty-state">
            <p>No property items added</p>
          </div>
        ) : (
          items.map((item) => (
            <div key={'mobile-' + item.tempid} className="card">
              <div className="card-header">
                <span className="card-item-name">{item.item || 'Unknown Item'}</span>
                <span className={'status-badge ' + getReportedAsClass(item)}>
                  {getReportedAsName(item) || 'Unknown'}
                </span>
              </div>

              <div className="card-grid">
                <div className="card-field">
                  <div className="card-field-label">Owner</div>
                  <div className="card-field-value">{getOwnerName(item)}</div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">Value</div>
                  <div className="card-field-value">{formatValue(item.estimatedValue)}</div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">Brand</div>
                  <div className="card-field-value">{item.brand || '\u2014'}</div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">Model</div>
                  <div className="card-field-value">{item.model || '\u2014'}</div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">Year</div>
                  <div className="card-field-value">{item.year || '\u2014'}</div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">Colors</div>
                  <div className="card-field-value">{item.colors || '\u2014'}</div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">Serial #</div>
                  <div className="card-field-value">{item.serialNum || '\u2014'}</div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">Location</div>
                  <div className="card-field-value">{item.location || '\u2014'}</div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">Storage</div>
                  <div className="card-field-value">{item.storageLoc || '\u2014'}</div>
                </div>
                <div className="card-field">
                  <div className="card-field-label">Damages</div>
                  <div className="card-field-value">{item.damages || '\u2014'}</div>
                </div>
                <div className="card-field card-field-full">
                  <div className="card-field-label">Notes</div>
                  <div className="card-field-value multiline-text">{item.officerNotes || '\u2014'}</div>
                </div>
              </div>

              {!isReadOnly && (
                <div className="card-actions">
                  <button className="btn-action btn-edit" onClick={() => editProperty(item)}>
                    <i className="fa fa-edit"></i> Edit
                  </button>
                  <button className="btn-action btn-delete" onClick={() => confirmDelete(item)}>
                    <i className="fa fa-trash"></i> Remove
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <ConfirmModal
        open={deleteConfirm.open}
        title="Remove Property?"
        messageHtml="Are you sure you want to remove this property item?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmClass="danger"
        theme={theme}
        onConfirm={doDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}

(window as any).CaseReportPropertyListReact = CaseReportPropertyList;
